import { Box, MenuItem, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import {
  GetAssemblyDetailsDocument,
  Maybe,
  useInlineUpdateSingleAssemblyTemplateFieldDataMutation,
} from '@/__generated__/graphql';
import { EquipmentDetailsTypography } from '../styles';
import { useAtomValue } from 'jotai';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useMemo, useState } from 'react';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { GraphQLErrors } from '@apollo/client/errors';

type Props = {
  name: string | undefined;
  value: Maybe<string> | undefined;
  type: string | undefined;
  templateKey: Maybe<string> | undefined;
  data?: { [key: string]: string };
};

export const SelectUpdater = ({ name, value, templateKey, data }: Props) => {
  const { showSnackBar } = useSnackBarMessage({
    variant: 'filled',
  });

  const [mutate, { loading }] = useInlineUpdateSingleAssemblyTemplateFieldDataMutation({
    refetchQueries: [GetAssemblyDetailsDocument],
  });

  const allAssemblyTemplates = useAtomValue(state.allAssemblyTemplates);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const selectedAssemblyId = useAtomValue(state.selectedAssemblyId);

  const template = allAssemblyTemplates?.find((t) => t.id === selectedAssemblyTemplateId);
  const field = template?.fields.find((f) => f.key === templateKey);

  // Check if the field has a detailFieldKey (assignment field) and get its value
  const subtitle = useMemo(() => {
    if (field?.detailFieldKey && data) {
      return data[field.detailFieldKey];
    }
    return undefined;
  }, [field?.detailFieldKey, data]);

  const initialValue = useMemo(() => {
    if (!value) return undefined;
    if (!field?.options?.includes(value)) return undefined;
    return value;
  }, [field?.options, value]);

  const [selectValue, setSelectValue] = useState<string | undefined>(initialValue);

  if (!field) return 'N/A';

  const onChange = async (event: SelectChangeEvent<string>) => {
    if (!selectedAssemblyId) {
      showSnackBar('Could not update. Missing selectedAssemblyId', 'error');
      return;
    }

    if (!templateKey) {
      showSnackBar('Could not update. Missing templateKey', 'error');
      return;
    }

    if (!selectedAssemblyTemplateId) {
      showSnackBar('Could not update. Missing selectedAssemblyTemplateId', 'error');
      return;
    }

    try {
      const response = await mutate({
        variables: {
          input: {
            assemblyId: selectedAssemblyId,
            key: templateKey,
            newValue: event.target.value,
            templateId: selectedAssemblyTemplateId,
            lastModifiedFor: `${name} update`,
          },
        },
      });
      const updatedValue =
        response?.data?.updateSingleTemplateFieldData?.templateFieldData?.[templateKey];
      if (updatedValue) {
        setSelectValue(updatedValue);
        showSnackBar('Updated successfully', 'success');
      } else {
        showSnackBar('Could not update. Invalid response from server', 'error');
      }
    } catch (error: unknown) {
      const gqlErrors = error as GraphQLErrors;
      const first = gqlErrors?.[0];
      showSnackBar(first?.message || 'Something went wrong. Please contact support', 'error');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        my: 1,
      }}
    >
      <Stack direction="column" alignItems="flex-start">
        <EquipmentDetailsTypography>{name}</EquipmentDetailsTypography>
        {subtitle && (
          <Typography
            fontSize={'1rem'}
            color="text.secondary"
            sx={{ fontWeight: 400, mt: 0.25 }}
          >
            ({subtitle})
          </Typography>
        )}
      </Stack>
      <Select
        disabled={loading}
        // TODO: Figure out how to add placeholders in MUI 6
        // placeholder="Select"
        value={selectValue ?? ''}
        onChange={onChange}
        sx={{
          minWidth: '100px',
          textOverflow: 'ellipsis',
          width: '50%',
          fontSize: '1.2rem',
          height: '2.5rem',
        }}
      >
        {field.options?.map((option) => (
          <MenuItem value={option} key={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};
